class WLSCart extends HTMLElement {
    constructor() {
      super();

      this.isUpdating = false;
      this.cartSlider = null;
      this.emptySlider = null;

      this.onClick = this.onClick.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onResize = this.onResize.bind(this);
    }

    /* -----------------------------
     * Lifecycle
     * ----------------------------- */

    connectedCallback() {
      this.addEventListener('click', this.onClick);
      this.addEventListener('submit', this.onSubmit);
      window.addEventListener('resize', this.onResize);

      this.bootstrap();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
    }

    bootstrap() {
        this.destroySliders();             // ensure no stale instances
        this.handleEmptyCartSlider();      // empty-cart slider is independent
        this.moveTestimonial();
      
        // Cart upsell slider: only initialize after slides exist
        const list = this.querySelector('.js-cart-page-upsell');
        const hasSlides = list && list.children.length > 0;
      
        if (hasSlides) {
          this.initCartSlider({ force: true });
        } else {
          this.renderRecommendations();    // this will init slider after injection
        }
      }

    /* -----------------------------
     * Helpers
     * ----------------------------- */

    getSectionWrapper() {
      return this.closest('[id^="shopify-section-"]');
    }

    destroySliders() {
        if (this.cartSlider) {
          this.cartSlider.destroy(true);
          this.cartSlider = null;
        }
        // do NOT destroy emptySlider here, it’s managed by handleEmptyCartSlider()
    }

    setLoading(isLoading) {
        this.classList.toggle('is-loading', isLoading);
    }   

    getSectionId() {
      const wrapper = this.getSectionWrapper();
      return wrapper ? wrapper.id.replace('shopify-section-', '') : null;
    }

    getRow(target) {
      return target.closest('.wls-cart__item');
    }

    getItemKey(row) {
      return row?.dataset?.key || null;
    }

    getQtyInput(row) {
      return (
        row.querySelector('.js-qty-input') ||
        row.querySelector('input[name="quantity"]')
      );
    }

    /* -----------------------------
     * Events
     * ----------------------------- */

    onClick(e) {
      const plus = e.target.closest('.wls-cart__qty-plus');
      const minus = e.target.closest('.wls-cart__qty-minus');
      const remove = e.target.closest('.wls-cart__remove');

      if (!plus && !minus && !remove) return;

      const row = this.getRow(e.target);
      if (!row) return;

      const key = this.getItemKey(row);
      if (!key) {
        console.warn(NS, 'Missing data-key on cart item row', row);
        return;
      }

      const input = this.getQtyInput(row);
      if (!input) {
        console.warn(NS, 'Quantity input not found for cart item', row);
        return;
      }

      let qty = parseInt(input.value, 10);
      if (Number.isNaN(qty)) qty = 0;

      if (plus) qty += 1;
      if (minus) qty -= 1;
      if (remove) qty = 0;

      qty = Math.max(0, qty);
      input.value = qty;

      this.changeItemQuantity(key, qty);
    }

    onSubmit(e) {
      const form = e.target.closest('form[action^="/cart/add"]');
      if (!form) return;

      e.preventDefault();
      this.addFromForm(form);
    }

    onResize() {
      this.handleEmptyCartSlider();
      this.moveTestimonial();
    }

    /* -----------------------------
     * Cart API
     * ----------------------------- */

    async changeItemQuantity(key, quantity) {
      if (this.isUpdating) return;
      this.isUpdating = true;
      this.setLoading(true);

      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({
            id: key,
            quantity,
          }),
        });

        if (!res.ok) {
          const text = await res.text();
          console.error(NS, 'Failed to change item quantity', { key, quantity }, text);
          return;
        }

        await this.refreshSection();
      } catch (e) {
        console.error(NS, 'Cart change request failed', e);
      } finally {
        this.isUpdating = false;
        this.setLoading(false);
      }
    }

    async addFromForm(form) {
      if (this.isUpdating) return;
      this.isUpdating = true;
      this.setLoading(true);
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          body: new FormData(form),
          credentials: 'same-origin',
        });

        if (!res.ok) {
          const text = await res.text();
          console.error(NS, 'Add to cart failed', text);
          return;
        }

        await this.refreshSection();
      } catch (e) {
        console.error(NS, 'Add to cart request failed', e);
      } finally {
        this.isUpdating = false;
        this.setLoading(false);
      }
    }

    /**
     * Re-renders the entire Shopify section wrapper.
     * This preserves all blocks and prevents layout loss.
     */
    async refreshSection() {
      const sectionId = this.getSectionId();
      if (!sectionId) {
        console.warn(NS, 'Unable to determine section ID for refresh');
        return;
      }

      const res = await fetch(
        `${window.location.pathname}?sections=${sectionId}`,
        { credentials: 'same-origin' }
      );

      const json = await res.json();
      const html = json[sectionId];
      if (!html) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newWrapper = doc.querySelector('[id^="shopify-section-"]');
      const currentWrapper = this.getSectionWrapper();

      if (newWrapper && currentWrapper) {
        currentWrapper.replaceWith(newWrapper);
      }
    }

    /* -----------------------------
     * Recommendations
     * ----------------------------- */

    async renderRecommendations() {
      const list = this.querySelector('.js-cart-page-upsell');
      if (!list || list.children.length) return;

      const firstRow = this.querySelector('.wls-cart__item');
      const productId = firstRow?.dataset?.productId;
      if (!productId) return;

      try {
        const res = await fetch(
          `/recommendations/products?section_id=wls-cart-recommendation-item&product_id=${productId}&limit=8`
        );

        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const items = doc.querySelectorAll('.wls-cart__overview-slide');

        items.forEach(item => list.appendChild(item));
        this.initCartSlider();
      } catch (e) {
        console.error(NS, 'Failed to load recommendations', e);
      }
    }

    /* -----------------------------
     * Splide – Cart slider
     * ----------------------------- */

    initCartSlider() {
      const el = this.querySelector('#wls-cart-slider');
      if (!el || !window.Splide || this.cartSlider) return;

      this.cartSlider = new Splide(el, {
        type: 'slide',
        perPage: 1,
        gap: 16,
        arrows: true,
        pagination: false,
      });

      this.cartSlider.mount();

      const arrows = el.querySelector('.splide__arrows');
      const headerArrows = this.querySelector('.wls-cart__overview-slider-arrows');
      if (arrows && headerArrows) headerArrows.appendChild(arrows);
    }

    /* -----------------------------
     * Splide – Empty cart slider
     * ----------------------------- */

    handleEmptyCartSlider() {
      const el = this.querySelector('#wls-cart-empty-slider');
      if (!el || !window.Splide) return;

      if (window.innerWidth < 768 && !this.emptySlider) {
        this.emptySlider = new Splide(el, {
          type: 'slide',
          perPage: 1,
          gap: 16,
          arrows: true,
          pagination: false,
        });

        this.emptySlider.mount();

        const arrows = el.querySelector('.splide__arrows');
        const headerArrows = this.querySelector('.wls-cart-empty__slider-arrows');
        if (arrows && headerArrows) headerArrows.appendChild(arrows);
      }

      if (window.innerWidth >= 768 && this.emptySlider) {
        this.emptySlider.destroy(true);
        this.emptySlider = null;
      }
    }

    /* -----------------------------
     * Testimonial relocation
     * ----------------------------- */

    moveTestimonial() {
      const testimonial = this.querySelector('.wls-cart__testimonial-holder');
      const overview = this.querySelector('.wls-cart__overview');
      const left = this.querySelector('.wls-cart__left');

      if (!testimonial || !overview || !left) return;

      if (window.innerWidth < 992) {
        if (!overview.contains(testimonial)) {
          overview.after(testimonial);
        }
      } else {
        if (!left.contains(testimonial)) {
          left.appendChild(testimonial);
        }
      }
    }
  }

  customElements.define('wls-cart', WLSCart);